import {
  CHAT_STATUS,
  COUNTDOWN_DANGER_SECONDS,
  COUNTDOWN_WARN_SECONDS,
} from '../config/constants';
import type { Chat, ChatCountDetails, ChatStatusTab } from '../model/schema';
import { firstFilled } from './actors';

/**
 * Pure chat-state rules lifted from cpanel `ChatRoom` / `ChatWindow` /
 * `ChatItem` / `useChatPage`: who may act on a chat, what the status means, the
 * support-tag badges, the reply timer and the tab counts.
 */

// ── status ──────────────────────────────────────────────────────────────

/** Closed — resolved (3) or not resolved (4). */
export function isChatClosed(chat: Pick<Chat, 'status'> | undefined): boolean {
  return (
    chat?.status === CHAT_STATUS.CLOSED_RESOLVED ||
    chat?.status === CHAT_STATUS.CLOSED_NOT_RESOLVED
  );
}

export type ChatStatusKey = 'new' | 'inProgress' | 'closed';

/** List-dot status: 2 → in progress, 1 → new, anything else → closed. */
export function chatStatusKey(status: number | null | undefined): ChatStatusKey {
  if (status === CHAT_STATUS.ACQUIRED) return 'inProgress';
  if (status === CHAT_STATUS.NEW) return 'new';
  return 'closed';
}

/** The row preview: the last message, else the chat subject. */
export function chatPreview(chat: Chat): string | undefined {
  return firstFilled(chat.last_message?.message, chat.subject);
}

// ── permissions ─────────────────────────────────────────────────────────

interface ActingUser {
  id: string | number | null | undefined;
  role: string | null | undefined;
}

/** The signed-in user owns the chat (`chat.responsible.id === user.id`). */
export function isResponsibleOperator(chat: Chat | undefined, user: ActingUser): boolean {
  const responsibleId = chat?.responsible?.id;
  if (responsibleId == null || user.id == null) return false;
  return String(responsibleId) === String(user.id);
}

/**
 * Hand-over rule: the owner — or a MANAGER acting as dispatcher — may reassign
 * a chat that is still open (status 3/4 are final).
 */
export function canSwitchOperator(chat: Chat | undefined, user: ActingUser): boolean {
  const isManager = String(user.role ?? '').toUpperCase() === 'MANAGER';
  return (isResponsibleOperator(chat, user) || isManager) && !isChatClosed(chat);
}

/** Only the owner closes a chat. */
export function canCloseChat(chat: Chat | undefined, user: ActingUser): boolean {
  return isResponsibleOperator(chat, user);
}

/**
 * A chat that already has a responsible operator can be answered straight away;
 * otherwise replying first asks to take the chat (`acquire`).
 */
export function isChatAcquired(chat: Chat | undefined): boolean {
  return Boolean(chat?.responsible) && Boolean(chat?.id);
}

// ── support tags ────────────────────────────────────────────────────────

export interface SupportBadge {
  id: string;
  label: string;
  type: 'category' | 'topic';
}

/** Category + topic-path chips of a support chat (cpanel `ChatItem`). */
export function buildSupportBadges(chat: Chat): SupportBadge[] {
  const support = chat.support;
  const category = support?.category;
  const topics = (support?.topic_path ?? support?.topics ?? [])
    .map((topic) => topic.title)
    .filter((title): title is string => Boolean(title));

  return [
    ...(category?.title
      ? [
          {
            id: `category-${category.id ?? category.title}`,
            label: category.title,
            type: 'category' as const,
          },
        ]
      : []),
    ...topics.map((topic, index) => ({
      id: `topic-${index}-${topic}`,
      label: topic,
      type: 'topic' as const,
    })),
  ];
}

// ── reply timer ─────────────────────────────────────────────────────────

export type CountdownTone = 'ok' | 'warn' | 'danger';

/** Green < 90 s, yellow from 90 s, red from 120 s (cpanel `MessageCountdown`). */
export function countdownTone(secondsPassed: number): CountdownTone {
  if (secondsPassed >= COUNTDOWN_DANGER_SECONDS) return 'danger';
  if (secondsPassed >= COUNTDOWN_WARN_SECONDS) return 'warn';
  return 'ok';
}

/** `mm:ss` of an elapsed duration (minutes wrap at the hour, like dayjs.duration). */
export function formatElapsed(secondsPassed: number): string {
  const total = Math.max(0, Math.floor(secondsPassed));
  const minutes = Math.floor(total / 60) % 60;
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// ── tab counts ──────────────────────────────────────────────────────────

/**
 * Badge per status tab from `count_details` (cpanel `chatCounts`): Новые =
 * open, В прогрессе = acquired, Все = the sum of all four, Решено = closed &
 * resolved, Не решено = closed & unresolved. Undefined until the first page loads.
 */
export function statusTabCounts(
  details: ChatCountDetails | null | undefined,
): Record<ChatStatusTab, number> | undefined {
  if (!details) return undefined;
  const open = details.open_count ?? 0;
  const acquired = details.acquired_count ?? 0;
  const resolved = details.closed_resolved_count ?? 0;
  const unresolved = details.closed_not_resolved_count ?? 0;
  return {
    '1': open,
    '2': acquired,
    ALL: open + acquired + resolved + unresolved,
    '3': resolved,
    '4': unresolved,
  };
}
